import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Iter 55 — Cricket Timeline (Gully Plus).
 *
 * The founder's principle: this is a professional cricket journal,
 * not a dashboard. No graphs, no badges, no XP, no streaks, no social
 * feed. Just the player's events in chronological order — newest
 * first — with simple filters and keyword search.
 *
 * Non-Plus users hitting this route get 402 from the backend; the
 * page detects that and redirects to /plus?reason=timeline so the
 * upgrade screen knows what brought them.
 */

const FILTERS = [
  { key: "all",      label: "All" },
  { key: "matches",  label: "Matches" },
  { key: "practice", label: "Practice" },
  { key: "videos",   label: "Videos" },
  { key: "reviews",  label: "Reviews" },
];

const TYPE_META = {
  match_reflection:    { icon: "🏏", label: "Match Reflection" },
  practice_reflection: { icon: "🎯", label: "Practice" },
  video_review:        { icon: "🎥", label: "Video Review" },
  weekly_review:       { icon: "📈", label: "Weekly Review" },
  monthly_review:      { icon: "📊", label: "Monthly Review" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric",
      year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function Timeline() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [welcome, setWelcome] = useState(null); // {dismissed, first_seen_at}
  const firstVisitFiredRef = useRef(false);
  const openedFiredRef     = useRef(false);   // dedupe StrictMode double-mount

  // Telemetry helper — fire-and-forget, never throws.
  const track = useCallback((kind, payload = {}) => {
    api.post("/client-error", { kind, session_id: null, payload })
       .catch(() => {});
  }, []);

  // Plus gate (client-side). If we know the user is logged out, send
  // them to /login with return_to=/timeline; the page will retry.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?return_to=${encodeURIComponent("/timeline")}`, { replace: true });
    }
  }, [user, authLoading, navigate]);

  const fetchTimeline = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("type", opts.filter ?? filter);
      if (opts.search ?? search) params.set("q", opts.search ?? search);
      params.set("limit", "50");
      const { data } = await api.get(`/web/timeline?${params.toString()}`);
      setEntries(data.entries || []);
      setWelcome(data.welcome || null);
      // Telemetry — fire timeline_first_visit exactly once per user.
      if (data.welcome && !data.welcome.dismissed && !firstVisitFiredRef.current) {
        firstVisitFiredRef.current = true;
        track("timeline_first_visit", { first_seen_at: data.welcome.first_seen_at });
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        // Backend says: not Plus. Send them to the pricing page.
        navigate("/plus?reason=timeline", { replace: true });
        return;
      }
      setError(e?.response?.data?.detail || "Couldn't load your timeline.");
    } finally {
      setLoading(false);
    }
  }, [filter, search, navigate, track]);

  useEffect(() => {
    if (!user) return;
    if (!openedFiredRef.current) {
      openedFiredRef.current = true;
      track("timeline_opened");
    }
    fetchTimeline();
    // We intentionally only depend on `user` here — filter/search
    // changes call fetchTimeline directly with the new value to
    // avoid the stale-closure debounce dance.
     
  }, [user]);

  const onFilterChange = (key) => {
    if (key === filter) return;
    setFilter(key);
    track("timeline_filter_changed", { filter: key });
    fetchTimeline({ filter: key });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    setSearch(trimmed);
    if (trimmed) track("timeline_search_used", { query_len: trimmed.length });
    fetchTimeline({ search: trimmed });
  };

  const onEntryOpen = (entry) => {
    track("timeline_entry_opened", { type: entry.type, entry_id: entry.entry_id });
    // For now every entry's deepest link is the chat conversation it
    // came from. Future iterations can route video/weekly entries to
    // dedicated detail screens.
    if (entry.conversation_id) {
      navigate(`/chat?session=${encodeURIComponent(entry.conversation_id)}`);
    } else {
      navigate("/chat");
    }
  };

  const onDismissWelcome = async () => {
    setWelcome((w) => (w ? { ...w, dismissed: true } : w));
    try { await api.post("/web/timeline/welcome-dismiss", {}); } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-100" data-testid="timeline-screen">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0A0A0A]/85 border-b border-neutral-800">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/chat" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="timeline-back-chat">
            <ArrowLeft size={16} /> Chat
          </Link>
          <h1 className="font-display text-base tracking-tight" data-testid="timeline-heading">
            Cricket Timeline
          </h1>
          <span className="text-xs text-emerald-400/80">Plus</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pb-24 pt-5">
        {/* Welcome card — once per user */}
        {welcome && !welcome.dismissed && (
          <section
            data-testid="timeline-welcome-card"
            className="relative mb-6 rounded-2xl border border-emerald-700/40 bg-emerald-950/20 p-5"
          >
            <button
              data-testid="timeline-welcome-dismiss"
              onClick={onDismissWelcome}
              className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-300"
              aria-label="Dismiss welcome"
            >
              <X size={16} />
            </button>
            <p className="font-display text-lg mb-2">Welcome to Gully Plus.</p>
            <p className="text-sm text-neutral-300 leading-relaxed">
              This is your cricket journey. Every match, practice session
              and video becomes part of one continuous story.
            </p>
            <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
              The more you use Gully, the more valuable this becomes.
            </p>
          </section>
        )}

        {/* Iter 60 — Insights drawer (Plus only). Slim toggle that
            opens a side surface with weekly review, monthly review,
            patterns, and confidence timeline. Reuses chat-trigger
            content via the same backend endpoints. */}
        <InsightsDrawer />

        {/* Search */}
        <form onSubmit={onSearchSubmit} className="mb-3 flex items-center gap-2" data-testid="timeline-search-form">
          <div className="flex-1 flex items-center gap-2 rounded-xl border border-neutral-800 bg-neutral-950 px-3 py-2 focus-within:border-emerald-700/60">
            <Search size={14} className="text-neutral-500" />
            <input
              data-testid="timeline-search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search your journey…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-600"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setSearch(""); fetchTimeline({ search: "" }); }}
                className="text-neutral-500 hover:text-neutral-300"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </form>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" data-testid="timeline-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`timeline-filter-${f.key}`}
              onClick={() => onFilterChange(f.key)}
              className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filter === f.key
                  ? "border-emerald-500/70 bg-emerald-900/30 text-emerald-200"
                  : "border-neutral-800 text-neutral-400 hover:text-neutral-200 hover:border-neutral-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-neutral-500" data-testid="timeline-loading">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-900/40 bg-red-950/20 p-4 text-sm text-red-300" data-testid="timeline-error">
            {error}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-neutral-500" data-testid="timeline-empty">
            <p className="font-display text-base mb-2">Nothing here yet.</p>
            <p className="text-sm text-neutral-600 max-w-sm mx-auto leading-relaxed">
              Tell Gully about a match, practice or upload a video — it&apos;ll show up here.
            </p>
            <Link to="/chat" className="inline-block mt-5 btn-primary text-xs">Open chat</Link>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="timeline-entries">
            {entries.map((entry) => (
              <li
                key={entry.entry_id}
                data-testid={`timeline-entry-${entry.type}`}
                className="rounded-xl border border-neutral-800 bg-neutral-950 p-4 hover:border-neutral-700 transition-colors"
              >
                <button
                  onClick={() => onEntryOpen(entry)}
                  className="w-full text-left"
                  data-testid={`timeline-open-${entry.entry_id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl leading-none mt-0.5" aria-hidden>
                      {(TYPE_META[entry.type] || {}).icon || "•"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] text-neutral-500 mb-1">
                        <span>{formatDate(entry.timestamp)}</span>
                        <span>·</span>
                        <span>{(TYPE_META[entry.type] || {}).label || entry.type}</span>
                      </div>
                      <p className="font-display text-sm text-neutral-100 mb-1 truncate">
                        {entry.title}
                      </p>
                      {entry.summary && (
                        <p className="text-sm text-neutral-400 leading-snug line-clamp-2">
                          {entry.summary}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}


// ============================================================================
// Iter 60 — Insights drawer (Plus only).
// Founder choice 1c (hybrid). Chat triggers stay primary; this drawer
// gives Plus subscribers a visual surface for the four insight types
// from inside Timeline. Lazy-loads each tab on first open — no work
// happens until the user actually expands the drawer.
// ============================================================================
const INSIGHTS_TABS = [
  { key: "weekly",     label: "Weekly" },
  { key: "monthly",    label: "Monthly" },
  { key: "patterns",   label: "Patterns" },
  { key: "confidence", label: "Confidence" },
];

function InsightsDrawer() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("weekly");
  return (
    <section className="mb-4" data-testid="timeline-insights">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 rounded-xl border border-neutral-800 bg-neutral-950/50 hover:border-emerald-700/40 hover:bg-emerald-950/10 px-3 py-2 transition-colors"
        data-testid="timeline-insights-toggle"
      >
        <span className="text-sm text-neutral-200 font-medium">Insights</span>
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">
          {open ? "hide" : "show"} · Plus
        </span>
      </button>
      {open && (
        <div
          className="mt-2 rounded-xl border border-neutral-800 bg-neutral-950/40"
          data-testid="timeline-insights-panel"
        >
          <div className="flex gap-1 px-2 pt-2 border-b border-neutral-900">
            {INSIGHTS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`text-xs px-3 py-1.5 rounded-t-md transition-colors ${
                  tab === t.key
                    ? "bg-emerald-950/40 text-emerald-200 border-b-2 border-emerald-500/70"
                    : "text-neutral-500 hover:text-neutral-300"
                }`}
                data-testid={`insights-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="p-3">
            {tab === "weekly"     && <InsightWeekly />}
            {tab === "monthly"    && <InsightMonthly />}
            {tab === "patterns"   && <InsightPatterns />}
            {tab === "confidence" && <InsightConfidence />}
          </div>
        </div>
      )}
    </section>
  );
}

function useLazyFetch(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(url)
      .then(({ data }) => { if (alive) { setData(data); setErr(null); } })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e.message || "failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url]);
  return { data, err, loading };
}

function _UpgradeNudge({ message }) {
  return (
    <p className="text-xs text-neutral-400 leading-relaxed" data-testid="insight-not-plus">
      {message || "This insight is part of Gully Plus."}
    </p>
  );
}

function _Spinner() {
  return (
    <div className="text-xs text-neutral-500 flex items-center gap-2 py-3">
      <Loader2 size={12} className="animate-spin" /> loading…
    </div>
  );
}

function InsightWeekly() {
  const { data, err, loading } = useLazyFetch("/me/insights/weekly");
  if (loading) return <_Spinner />;
  if (err) return <p className="text-xs text-red-400">{err}</p>;
  if (data && data.available === false && data.reason === "not_plus") {
    return <_UpgradeNudge message={data.message} />;
  }
  return (
    <pre
      className="whitespace-pre-wrap text-xs text-neutral-200 leading-relaxed font-sans"
      data-testid="insight-weekly-body"
    >
      {data?.message || "No weekly review yet."}
    </pre>
  );
}

function InsightMonthly() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [generating, setGenerating] = useState(false);
  const loadLatest = useCallback(() => {
    setLoading(true);
    api.get("/me/insights/monthly/latest")
      .then(({ data }) => { setLatest(data); setErr(null); })
      .catch((e) => { setErr(e?.response?.data?.detail || e.message); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadLatest(); }, [loadLatest]);
  async function onGenerate() {
    setGenerating(true);
    setErr(null);
    try {
      const { data } = await api.post("/me/insights/monthly/generate");
      setLatest(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "failed");
    } finally {
      setGenerating(false);
    }
  }
  if (loading) return <_Spinner />;
  if (err) return <p className="text-xs text-red-400">{err}</p>;
  if (latest && latest.available === false && latest.reason === "not_plus") {
    return <_UpgradeNudge message={latest.message} />;
  }
  const hasLatest = latest && latest.message;
  return (
    <div className="space-y-3">
      {hasLatest ? (
        <pre
          className="whitespace-pre-wrap text-xs text-neutral-200 leading-relaxed font-sans"
          data-testid="insight-monthly-body"
        >
          {latest.message}
        </pre>
      ) : (
        <p className="text-xs text-neutral-500">
          {latest?.reason === "no_history"
            ? "No monthly review generated yet. Tap the button to create your first one."
            : "Generate your monthly cricket review when you're ready."}
        </p>
      )}
      <button
        type="button"
        onClick={onGenerate}
        disabled={generating}
        className="text-xs px-3 py-1.5 rounded-lg border border-emerald-700/60 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-50"
        data-testid="insight-monthly-generate"
      >
        {generating ? "generating…" : (hasLatest ? "regenerate monthly review" : "generate monthly review")}
      </button>
    </div>
  );
}

function InsightPatterns() {
  const { data, err, loading } = useLazyFetch("/me/insights/patterns");
  if (loading) return <_Spinner />;
  if (err) return <p className="text-xs text-red-400">{err}</p>;
  if (data && data.available === false && data.reason === "not_plus") {
    return <_UpgradeNudge message={data.summary_line} />;
  }
  if (!data?.patterns?.length) {
    return <p className="text-xs text-neutral-400">{data?.summary_line || "No patterns surfaced yet."}</p>;
  }
  return (
    <div className="space-y-2" data-testid="insight-patterns-body">
      <p className="text-xs text-neutral-400">{data.summary_line}</p>
      <ul className="space-y-1.5">
        {data.patterns.map((p) => (
          <li key={p.pattern_id || p.description} className="text-xs text-neutral-200 leading-relaxed">
            <span className="text-emerald-300/80 mr-1.5">·</span>
            {p.description}
            <span className="ml-2 text-neutral-500 text-[10px]">
              seen {p.evidence_count}× · {p.category}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsightConfidence() {
  const { data, err, loading } = useLazyFetch("/me/insights/confidence");
  if (loading) return <_Spinner />;
  if (err) return <p className="text-xs text-red-400">{err}</p>;
  if (data && data.available === false && data.reason === "not_plus") {
    return <_UpgradeNudge message={data.summary_line} />;
  }
  if (!data?.points?.length) {
    return <p className="text-xs text-neutral-400">{data?.summary_line || "Not enough confidence signal yet."}</p>;
  }
  const dirLabel = data.current_direction
    ? data.current_direction.toUpperCase()
    : "TBD";
  const dirColor = data.current_direction === "rising" ? "text-emerald-300"
                  : data.current_direction === "falling" ? "text-red-400"
                  : "text-neutral-400";
  return (
    <div className="space-y-3" data-testid="insight-confidence-body">
      <p className="text-xs text-neutral-200 leading-relaxed">{data.summary_line}</p>
      <div className="flex gap-3 text-[11px]">
        <div className="flex-1 rounded-lg border border-neutral-900 bg-neutral-950/60 p-2">
          <div className="uppercase text-neutral-500 tracking-wider text-[9px] mb-0.5">Direction</div>
          <div className={`font-mono ${dirColor}`}>{dirLabel}</div>
        </div>
        {data.lowest && (
          <div className="flex-1 rounded-lg border border-neutral-900 bg-neutral-950/60 p-2">
            <div className="uppercase text-neutral-500 tracking-wider text-[9px] mb-0.5">Lowest</div>
            <div className="font-mono text-neutral-300">{data.lowest.date_iso}</div>
          </div>
        )}
        {data.highest && (
          <div className="flex-1 rounded-lg border border-neutral-900 bg-neutral-950/60 p-2">
            <div className="uppercase text-neutral-500 tracking-wider text-[9px] mb-0.5">Highest</div>
            <div className="font-mono text-neutral-300">{data.highest.date_iso}</div>
          </div>
        )}
      </div>
      {/* Sparkline — minimal SVG, no chart library. */}
      <_Sparkline points={data.points} />
    </div>
  );
}

function _Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const w = 320, h = 60, pad = 4;
  const xs = points.map((_, i) => pad + (i * (w - 2 * pad)) / (points.length - 1));
  // score is -1..+1 → map to y so +1 is top.
  const ys = points.map((p) => h - pad - ((p.score + 1) / 2) * (h - 2 * pad));
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${ys[i].toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="block" data-testid="insight-confidence-sparkline">
      <line x1={pad} x2={w - pad} y1={h / 2} y2={h / 2}
            stroke="rgb(64,64,64)" strokeDasharray="2 3" />
      <path d={d} fill="none" stroke="rgb(110,231,183)" strokeWidth="1.5" />
      {points.map((p, i) => (
        <circle key={i} cx={xs[i]} cy={ys[i]} r={p.evidence_count > 0 ? 2.5 : 1.5}
                fill={p.evidence_count > 0 ? "rgb(110,231,183)" : "rgb(120,120,120)"} />
      ))}
    </svg>
  );
}

